import { observer } from "mobx-react";
import DateTimeV2, {
    DATETIME_VIEW,
} from "../../../../../components/datetime-v2/datetime-v2";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import FontBody3 from "../../../../../components/fonts/font-body3/font-body3";
import FontBody4 from "../../../../../components/fonts/font-body4/font-body4";
import {
    FONT_COLOR,
    FONT_VARIANT,
} from "../../../../../components/fonts/font-props";
import iconCircleEmail from "../../../../../components/icons/icon-circle-email/icon-circle-email";
import iconCircleError from "../../../../../components/icons/icon-circle-error/icon-circle-error";
import iconCircleNote from "../../../../../components/icons/icon-circle-note/icon-circle-note";
import { ICON_SIZE } from "../../../../../components/icons/icon-props-interface";
import Icon from "../../../../../components/icons/icon/icon";
import iconNewTab from "../../../../../components/icons/icon-new-tab/icon-new-tab";
import "./timeline-item.scss";
import TextTruncate from "../../../../../components/text-truncate/text-truncate";
import {
    RMA_EXTERNAL_TRANSACTION_STATUS,
    TIMELINE_ITEM_TYPE_ENUM,
} from "../../rma-types";
import TimelineItemStore from "../../stores/timeline-item-store";
import IconCredited from "../../../../../components/icons/icon-credited/icon-credited";
import IconRefunded from "../../../../../components/icons/icon-refunded/icon-refunded";
import Chip, { CHIP_COLORS } from "../../../../../components/chip/chip";
import { ButtonIcon } from "../../../../../components/inputs/button-icon/button-icon";
import currentAccountStore from "../../../../../pages/accounts/stores/current-account-store";
import { Tooltip } from "../../../../../components/tooltip/tooltip";
import { useDateTimeFormattingParams } from "../../../../../hooks/use-datetime-formatting-params";
import TimelineClassifiedError from "./timeline-classified-error";

interface ITimelineMessageProps {
    store: TimelineItemStore;
    rmaId: number;
}
const TimelineItem: React.FC<ITimelineMessageProps> = observer(
    ({ store, rmaId }) => {
        const type = store.type;
        const icon = getItemIcon(type);
        const side: "left" | "right" =
            store.isSystem || store.isAutomation ? "left" : "right";
        const isError = type === TIMELINE_ITEM_TYPE_ENUM.TYPE_ERROR;
        const isSuperAdmin = currentAccountStore.isSysAdmin;
        const dateTimeParams = useDateTimeFormattingParams();

        const handleLogs = () => {
            store.generateLogsUrl(rmaId);
        };

        const getFormattedContent = () => {
            const escapeHtml = (text: string) => {
                const div = document.createElement('div');
                div.textContent = text;
                return div.innerHTML;
            };

            // Content is formatted as "line1##line2##line3"
            // Delimiter is defined in the backend in ReturnErrorsService when creating an RMA note.
            // It is required to properly display RMA notes with vendor context.
            const delimiter = '##';
            const content = store.content.split(delimiter);
            return content.map((line, index) =>
                `<div key="${index}">${escapeHtml(line)}</div>`
            ).join('');
        };

        return (
            <div
                className={
                    "timeline-message-container place-" +
                    side +
                    (isError ? " timeline-error" : "")
                }
            >
                <div className={"timeline-message-icon"}>{icon}</div>
                <div className={"timeline-message-box"}>
                    <div className={"box-header"}>
                        <div className={"header-left"}>
                            <FontBody4
                                color={FONT_COLOR.PRIMARY2}
                                variant={FONT_VARIANT.BOLD}
                            >
                                <DateTimeV2
                                    {...dateTimeParams}
                                    utcDateString={store.createdAt}
                                    dateTimeView={DATETIME_VIEW.TIME_ONLY}
                                />
                            </FontBody4>
                            {isSuperAdmin && (
                                <Tooltip
                                    content={
                                        <div className="cloudwatch-tooltip-content">
                                            <FontBody3
                                                color={
                                                    FONT_COLOR.NEUTRAL_1_WHITE
                                                }
                                                variant={FONT_VARIANT.DEFAULT}
                                            >
                                                Logs
                                            </FontBody3>
                                        </div>
                                    }
                                >
                                    <ButtonIcon
                                        Component={iconNewTab}
                                        size={ICON_SIZE.SMALL}
                                        onClick={handleLogs}
                                    />
                                </Tooltip>
                            )}
                        </div>
                        <FontBody4
                            color={FONT_COLOR.PRIMARY2}
                            variant={FONT_VARIANT.BOLD}
                        >
                            {store.userName}
                        </FontBody4>
                    </div>
                    <div className={"box-content"}>
                        <div className={"box-content-text"}>
                            <FontBody2 color={FONT_COLOR.PRIMARY2}>
                                { store.returnError?.classified_errors?.length ? (
                                    <TimelineClassifiedError
                                        returnError={store.returnError}
                                    />
                                ) : (
                                    <TextTruncate allowExpand={true} maxLines={3}>
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: isError
                                                    ? getFormattedContent()
                                                    : store.content,
                                            }}
                                        />
                                    </TextTruncate>
                                )}
                            </FontBody2>
                            {!!store.transactionId && (
                                <FontBody2 color={FONT_COLOR.PRIMARY2}>
                                    Transaction ID: {store.transactionId}
                                </FontBody2>
                            )}
                        </div>
                        {!!store.status && (
                            <div className="status-chip-container">
                                <Chip
                                    label={store.status}
                                    color={getTransactionStatusChipColor(
                                        store.status
                                    )}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
);
function getItemIcon(type: TIMELINE_ITEM_TYPE_ENUM) {
    const color = getItemIconColor(type);
    switch (type) {
        case TIMELINE_ITEM_TYPE_ENUM.TYPE_NOTE:
            return (
                <Icon
                    size={ICON_SIZE.MEDIUM}
                    Component={iconCircleNote}
                    color={color}
                />
            );
        case TIMELINE_ITEM_TYPE_ENUM.TYPE_CREDITED:
            return (
                <Icon
                    size={ICON_SIZE.MEDIUM}
                    Component={IconCredited}
                    color={color}
                />
            );
        case TIMELINE_ITEM_TYPE_ENUM.TYPE_REFUNDED:
            return (
                <Icon
                    size={ICON_SIZE.MEDIUM}
                    Component={IconRefunded}
                    color={color}
                />
            );
        case TIMELINE_ITEM_TYPE_ENUM.TYPE_ERROR:
            return (
                <Icon
                    size={ICON_SIZE.MEDIUM}
                    Component={iconCircleError}
                    color={color}
                />
            );
        case TIMELINE_ITEM_TYPE_ENUM.TYPE_EMAIL:
            return (
                <Icon
                    size={ICON_SIZE.MEDIUM}
                    Component={iconCircleEmail}
                    color={color}
                />
            );
    }
}

function getItemIconColor(type: TIMELINE_ITEM_TYPE_ENUM) {
    switch (type) {
        case TIMELINE_ITEM_TYPE_ENUM.TYPE_NOTE:
            return "var(--color-accent-1)";
        case TIMELINE_ITEM_TYPE_ENUM.TYPE_CREDITED:
            return "var(--color-accent-1)";
        case TIMELINE_ITEM_TYPE_ENUM.TYPE_REFUNDED:
            return "var(--color-accent-1)";
        case TIMELINE_ITEM_TYPE_ENUM.TYPE_ERROR:
            return "var(--color-accent-3)";
    }
}

const getTransactionStatusChipColor = (
    status: RMA_EXTERNAL_TRANSACTION_STATUS
) => {
    switch (status) {
        case RMA_EXTERNAL_TRANSACTION_STATUS.PLATFORM_ORDER_TRANSACTION_STATUS_SUCCESS:
            return CHIP_COLORS.GREEN;
        case RMA_EXTERNAL_TRANSACTION_STATUS.PLATFORM_ORDER_TRANSACTION_STATUS_PENDING:
        case RMA_EXTERNAL_TRANSACTION_STATUS.PLATFORM_ORDER_TRANSACTION_STATUS_AWAITING_RESPONSE:
            return CHIP_COLORS.ORANGE;
        default:
            return CHIP_COLORS.RED;
    }
};
export default TimelineItem;
